import { HttpClient } from '@angular/common/http';
import { inject, Inject, Injectable } from '@angular/core';
import { GungImageType, GungImageUrlService, gungCheckFileExistsLocally } from 'gung-common';
import { AuthService } from '../auth/auth.service';
import { asapScheduler, filter, first, forkJoin, map, Observable, observeOn, of, switchMap } from 'rxjs';
import { GungFlowService } from '../gung-flow/gung-flow.service';
import { CartService } from '../cart/cart.service';
import { GungModalService } from '../gung-modal/gung-modal.service';
import { Router } from '@angular/router';
import { GungFlow } from '../../state/flow/types';
import { BaseViewConfigService, ProductViewType } from '../base-view-config/base-view-config.service';
import { Assortment, AssortmentService } from '../assortment.service';
import { TranslateService } from '@ngx-translate/core';
import { LocationConfigService } from '../location-config/location-config.service';
import { GungCompanyService } from '../gung-company/gung-company.service';
import { SelectedCustomerService } from '../selected-customer/selected-customer.service';
import { NavigationConfig } from '../../models/navigation-config';
import { User } from '../../state/auth/types';

export interface GungShopMenu {
  heading: string;
  link?: string;
  links?: GungShopMenu[];
  /**
   * A small picture for the entry, ready to put in an <img src>.
   *
   * <p>For an assortment it is the second image on the assortment - the first one is the category
   * banner. Undefined when the assortment has fewer than two images, and the menu then renders text
   * only, exactly as before.
   */
  icon?: string;
  action?: () => void;
  queryParams?: { [key: string]: string };
}

@Injectable({
  providedIn: 'root'
})
export class GungNavbarService {
  protected authService = inject(AuthService);
  protected gungCompanyService = inject(GungCompanyService);
  protected http = inject(HttpClient);
  protected selectedCustomerService = inject(SelectedCustomerService);
  protected gungFlowService = inject(GungFlowService);
  protected cartService = inject(CartService);
  protected gungModalService = inject(GungModalService);
  protected router = inject(Router);
  protected baseViewConfigService = inject(BaseViewConfigService);
  protected assortmentService = inject(AssortmentService);
  protected translateService = inject(TranslateService);
  protected locationConfigService = inject(LocationConfigService);
  protected gungImageUrlService = inject(GungImageUrlService);

  logoUrl: string;

  isAssortmentBased = !!this.environment?.isAssortmentBasedNavigation || false;
  flowsAsDropDrown = false;
  public assortmentMenuLevel = this.baseViewConfigService.assortmentMenuLevel;
  removeUserAssortmentTopLevel = false;
  getAssortmentMenuWithNoProducts = !!this.environment?.getAssortmentMenuWithNoProducts || this.baseViewConfigService.getAssortmentNoProducts || false;
  protected getAssortmentWithFlowFilter: boolean = this.baseViewConfigService.getAssortmentWithFlowFilter;

  currentLang = this.translateService.currentLang || 'se';
  navbarHeight = 115;
  forceToNotShowFlowsOnTop = false;
  hideMyCustomers = false;
  hideFavorites = false;
  hideCart = false;
  hideSelectedCustomer = false;
  // A user who is not sales has no "My customers" icon, so a shortcut to their own account has
  // nowhere to live in the icon row unless the shop asks for one.
  showMyAccount = false;
  showLanguageSelection = false;

  showNavbarSearch: boolean = true;


  constructor(
    @Inject('environment')
    protected environment: NavigationConfig
  ) {
    // A cached company response arrives synchronously, i.e. still inside this constructor, and subclass field
    // initializers would then overwrite the company config
    this.applyCompanyNavbarConfig().pipe(observeOn(asapScheduler)).subscribe();
  }

  // Public because the navbar component has to wait for this to resolve before it reads the
  // flags below - they are all still at their defaults until the company config has arrived.
  public applyCompanyNavbarConfig(): Observable<void> {
    return this.gungCompanyService.getPublicGungCompany().pipe(
      first(),
      map(company => {
        const navbarConfig = company?.extra?.layout?.navbar;
        if (!navbarConfig) {
          return;
        }
        if (navbarConfig.type) {
          this.isAssortmentBased = navbarConfig.type === 'assortment';
        }
        if (navbarConfig.assortmentMenuLevel != null) {
          const level = navbarConfig.assortmentMenuLevel;
          this.assortmentMenuLevel = level === 'auto' ? 'auto' : Number(level);
        }
        if (navbarConfig.removeUserAssortmentTopLevel != null) {
          this.removeUserAssortmentTopLevel = navbarConfig.removeUserAssortmentTopLevel;
        }
        if (navbarConfig.forceToNotShowFlowsOnTop != null) {
          this.forceToNotShowFlowsOnTop = navbarConfig.forceToNotShowFlowsOnTop;
        }
        if (navbarConfig.hideMyCustomers != null) {
          this.hideMyCustomers = navbarConfig.hideMyCustomers;
        }
        if (navbarConfig.hideFavorites != null) {
          this.hideFavorites = navbarConfig.hideFavorites;
        }
        if (navbarConfig.hideCart != null) {
          this.hideCart = navbarConfig.hideCart;
        }
        if (navbarConfig.hideSelectedCustomer != null) {
          this.hideSelectedCustomer = navbarConfig.hideSelectedCustomer;
        }
        if (navbarConfig.showNavbarSearch != null) {
          this.showNavbarSearch = navbarConfig.showNavbarSearch;
        }
        if (navbarConfig.showMyAccount != null) {
          this.showMyAccount = navbarConfig.showMyAccount;
        }
        if (navbarConfig.showLanguageSelection != null) {
          this.showLanguageSelection = navbarConfig.showLanguageSelection;
        }
      })
    );
  }

  getUserRole(roles: string[]): string {
    if (roles.includes('ADMIN')) {
      return 'ADMIN';
    } else if (roles.includes('SALES')) {
      return 'SALES';
    } else if (roles.includes('USER') && roles.length > 1) {
      return roles.filter(role => role !== 'USER')[0];
    } else if (roles.includes('USER') && roles.length === 1) {
      return 'USER';
    } else {
      return '';
    }
  }

  public getLogoUrl() {
    return this.logoUrl;
  }

  public getLogoUrlObservable(): Observable<string> {
    return this.gungCompanyService.getLogoUrlObservable().pipe(
      switchMap(logo =>
        forkJoin({
          logo: of(logo),
          assetsLogoExists: gungCheckFileExistsLocally(this.getLogoUrl(), this.http, false).pipe(first())
        })
      ),
      map(({ logo, assetsLogoExists }) => {
        return logo || (assetsLogoExists ? this.getLogoUrl() : 'https://cdn3.gung.io/_gung_common/_design/logo.png');
      })
    );
  }

  getCustomerInfo(): Observable<{ id: string; description: string }> {
    return this.selectedCustomerService.getSelectedCustomer().pipe(
      filter(cust => !!cust),
      map(selectedCustomer => ({
        id: selectedCustomer.id,
        description: `${selectedCustomer.extra?.gme?.displayId || selectedCustomer.id} - ${selectedCustomer.extra?.gme?.displayName || selectedCustomer.name}`
      }))
    );
  }

  getShopMenu(): Observable<GungShopMenu[]> {
    this.currentLang = this.translateService.currentLang || 'se';
    return this.applyCompanyNavbarConfig().pipe(
      switchMap(() => this.isAssortmentBased ? this.getAssortmentMenu() : this.getFlowMenu())
    );
  }

  public getFlowMenu(): Observable<GungShopMenu[]> {
    return this.gungFlowService.getGungFlows().pipe(
      map(gungFlow => {
        const baseMenu: GungShopMenu[] = [];
        for (const flow of gungFlow) {
          if (!flow.extra.digitalAssets) {
            baseMenu.push({
              heading: flow.name,
              action: () => {
                this.selectFlow(flow.id);
                this.redirectToProducts();

              }
            });
          }
        }
        return baseMenu;
      })
    );
  }

  protected getNavbarAssortmentRecursive(user: User): Observable<Assortment> {
    if (this.getAssortmentWithFlowFilter) {
      return this.gungFlowService.getSelectedFlow().pipe(
        filter(flow => !!flow),
        first(),
        switchMap(flow => {
          if (this.getAssortmentMenuWithNoProducts) {
            return this.assortmentService.getAssortmentRecursiveNoProductsWithFlowFilter(user.assortment, flow.id).pipe(first());
          } else {
            return this.assortmentService.getAssortmentRecursiveWithFlowFilter(user.assortment, flow.id).pipe(first());
          }
        })
      );
    } else if (this.getAssortmentMenuWithNoProducts) {
      return this.assortmentService.getAssortmentRecursiveNoProducts(user.assortment).pipe(first());
    }

    return this.assortmentService.getAssortmentRecursive(user.assortment).pipe(first());
  }

  /**
   * The menu icon for an assortment: its second image, scaled down.
   *
   * <p>Index 1 on purpose - the first image is what lib-product-list-banner shows at the top of the
   * category page, so an assortment without a dedicated icon simply has none.
   */
  protected getAssortmentMenuIcon(assort: Assortment): string | undefined {
    const image = assort?.extra?.images?.[1];
    if (!image?.s3Uri) {
      return undefined;
    }
    return this.gungImageUrlService.getUrl(image.s3Uri, {
      etag: image.s3ETag,
      type: GungImageType.list
    });
  }

  getAssortmentMenu(): Observable<GungShopMenu[]> {
    return this.authService.getCurrentUser().pipe(
      filter(user => !!user),
      first(),
      switchMap(user => this.getNavbarAssortmentRecursive(user).pipe(first())),
      map(assortment => {
        if (typeof this.assortmentMenuLevel === 'number') {
          if (this.assortmentMenuLevel === 0) {
            return assortment.children.map(childAssortment => {
              const link = {
                heading:
                  (this.currentLang && childAssortment.extra?.i18n?.[this.currentLang]?.assortmentName) ||
                  childAssortment.name,
                action: () => {
                  this.cleanScroll();
                },
                link: this.getProductListBaseRoute() + `/${childAssortment.id}`
              } as GungShopMenu;
              return link;
            });
          } else {
            if (this.removeUserAssortmentTopLevel) {
              // add one more level to create menu object because we will remove 1st level(user assortment)
              this.assortmentMenuLevel = this.assortmentMenuLevel + 1;
              let menu = this.getAssortmentWithLevels(assortment);
              // remove level added to keep html with correct levels
              this.assortmentMenuLevel = this.assortmentMenuLevel - 1;
              return [...menu[0].links];
            }
            return this.getAssortmentWithLevels(assortment);
          }

        } else if (this.assortmentMenuLevel === 'auto') {
          let menu = this.getAssortmentMenuWithAutoLevel(assortment);
          if (menu?.[0]?.links?.length > 0 && this.removeUserAssortmentTopLevel) {
            menu = menu[0].links;
            this.assortmentMenuLevel = this.maxDepthReached;
          } else {
            this.assortmentMenuLevel = this.maxDepthReached + 1;
          }
          return menu
        }
      })
    );
  }

  private maxDepthReached = 0;

  getAssortmentMenuWithAutoLevel(assortments) {
    return [this.buildAssortmentNavigationAuto(assortments, 0, this.getProductListBaseRoute() + '/')];
  }
  protected buildAssortmentNavigationAuto(
    assort: Assortment,
    level: number = 0,
    lastPath: string = ''
  ): GungShopMenu | undefined {



    if (assort.assortment === false) {
      return undefined;
    }
    if (level > this.maxDepthReached) {
      this.maxDepthReached = level;
    }

    const hasChildren = assort?.children && assort.children.length > 0;

    if (hasChildren) {
      const path = level !== 0 ? `${lastPath}/${assort.id}` : lastPath;

      let link: string;
      if (this.baseViewConfigService.productViewType !== ProductViewType.assortmentTreeView) {
        link = `${this.getProductListBaseRoute()}/${assort.id}`;
      } else {
        link = path;
      }

      const links = assort.children
        .map(child => this.buildAssortmentNavigationAuto(child, level + 1, path))
        .filter((v): v is GungShopMenu => !!v);

      return {
        heading: (this.currentLang && assort.extra?.i18n?.[this.currentLang]?.assortmentName) || assort.name,
        link,
        links,
        icon: this.getAssortmentMenuIcon(assort)
      };
    } else {

      const path = level !== 0 ? `${lastPath}/${assort.id}` : lastPath;
      let link: string;
      if (this.baseViewConfigService.productViewType !== ProductViewType.assortmentTreeView) {
        link = `${this.getProductListBaseRoute()}/${assort.id}`;
      } else {
        link = path;
      }

      return {
        heading: (this.currentLang && assort.extra?.i18n?.[this.currentLang]?.assortmentName) || assort.name,
        link,
        links: undefined,
        icon: this.getAssortmentMenuIcon(assort)
      };
    }
  }

  getAssortmentWithLevels(assortments): GungShopMenu[] {
    return [this.buildAssortmentNavigation(assortments, 0, this.getProductListBaseRoute() + '/', this.assortmentMenuLevel)];
  }

  protected buildAssortmentNavigation(assort: Assortment, level: number = 0, lastPath: string = '', limit): GungShopMenu {
    if (assort.assortment && level < limit) {

      const path: string = (level !== 0) ? lastPath + '/' + assort.id : lastPath;
      let pathFix: string;
      if (this.baseViewConfigService.productViewType !== ProductViewType.assortmentTreeView) {
        pathFix = this.getProductListBaseRoute() + '/' + assort.id
      }
      const links = assort?.children.map(assort => this.buildAssortmentNavigation(assort, level + 1, path, limit)).filter(v => !!v);
      return {
        heading: (this.currentLang && assort.extra?.i18n?.[this.currentLang]?.assortmentName) || assort.name,
        link: (pathFix || path),
        links: links,
        icon: this.getAssortmentMenuIcon(assort)
      }
    } else {
      return undefined;
    }
  }

  selectFlow(flowId: string): void {
    forkJoin({ flow: this.gungFlowService.getSelectedFlow().pipe(first()), cart: this.cartService.getCurrentCart().pipe(first()) })
      .pipe(first())
      .subscribe(({ flow, cart }) => {
        if (flow.id === flowId) {
          return;
        }
        if (cart.length === 0) {
          this.gungFlowService.selectFlow(flowId);
          return;
        }
        this.gungModalService
          .openConfirmYesNoModal('FLOW_CHANGE', 'FLOW_CHANGE_CLEAR_CART_CONFIRM', {}, 'OK', 'CANCEL')
          .then(
            result => {
              if (result) {
                this.gungFlowService.selectFlow(flowId);
              }
            },
            reason => undefined
          );
      });
  }


  cleanScroll() {
    const locationPosition = this.locationConfigService.get('products');
    if (!!locationPosition) {
      this.locationConfigService.remove(locationPosition);
    }
  }

  redirectToProducts(flow?: GungFlow): void {
    this.router.navigate([this.getProductListBaseRoute()]);
  }

  getProductListBaseRoute(): string {
    if (this.baseViewConfigService.productViewType === ProductViewType.assortmentTreeView) {
      return '/articles';
    }
    return '/products';
  }
}
